/**
 * Hybrid Ed25519 + ML-DSA-65 signatures (AND combiner).
 *
 * New sends sign the same preimage with both algorithms. Verification
 * succeeds only if both halves succeed. Encoding is version-prefixed so a
 * 64-byte Ed25519 truncating verifier cannot treat a hybrid blob as
 * classical-only.
 *
 * Legacy 64-byte Ed25519 signatures remain valid via {@link verifySignature}.
 *
 * @module crypto/sign/hybrid
 */
import { ml_dsa65 } from '@noble/post-quantum/ml-dsa';
import { concatBytes, constantTimeEqual, randomBytes, toBytes, } from '../utils';
import { ED25519_PRIVATE_KEY_SIZE, ED25519_PUBLIC_KEY_SIZE, ED25519_SIGNATURE_SIZE, sign as signEd25519, verify as verifyEd25519, } from './ed25519';
/** UTF-8 domain prefix distinguishing hybrid signatures from raw Ed25519. */
export const HYBRID_SIGNATURE_PREFIX = toBytes('adieuu-hsig-v1');
/** FIPS 204 ML-DSA-65 public key size. */
export const ML_DSA_65_PUBLIC_KEY_SIZE = 1952;
/** FIPS 204 ML-DSA-65 secret key size. */
export const ML_DSA_65_SECRET_KEY_SIZE = 4032;
/** FIPS 204 ML-DSA-65 signature size. */
export const ML_DSA_65_SIGNATURE_SIZE = 3309;
/** prefix || Ed25519 (64) || ML-DSA-65 (3309). */
export const HYBRID_SIGNATURE_SIZE = HYBRID_SIGNATURE_PREFIX.length +
    ED25519_SIGNATURE_SIZE +
    ML_DSA_65_SIGNATURE_SIZE;
const PREFIX_LEN = HYBRID_SIGNATURE_PREFIX.length;
const ED25519_OFFSET = PREFIX_LEN;
const ML_DSA_OFFSET = PREFIX_LEN + ED25519_SIGNATURE_SIZE;
/**
 * True when `signature` is the versioned hybrid encoding (exact length + prefix).
 */
export function isHybridSignatureEncoding(signature) {
    if (signature.length !== HYBRID_SIGNATURE_SIZE) {
        return false;
    }
    return constantTimeEqual(signature.subarray(0, PREFIX_LEN), HYBRID_SIGNATURE_PREFIX);
}
/**
 * Signs `message` with Ed25519 AND ML-DSA-65 over the same preimage.
 *
 * @returns Version-prefixed hybrid signature ({@link HYBRID_SIGNATURE_SIZE} bytes)
 */
export function signHybrid(ed25519PrivateKey, mlDsa65SecretKey, message) {
    if (ed25519PrivateKey.length !== ED25519_PRIVATE_KEY_SIZE) {
        throw new Error(`Ed25519 private key must be ${ED25519_PRIVATE_KEY_SIZE} bytes, got ${ed25519PrivateKey.length}`);
    }
    if (mlDsa65SecretKey.length !== ML_DSA_65_SECRET_KEY_SIZE) {
        throw new Error(`ML-DSA-65 secret key must be ${ML_DSA_65_SECRET_KEY_SIZE} bytes, got ${mlDsa65SecretKey.length}`);
    }
    const ed25519Sig = signEd25519(ed25519PrivateKey, message);
    const mlDsaSig = ml_dsa65.sign(mlDsa65SecretKey, message, undefined, randomBytes(ml_dsa65.signRandBytes));
    if (mlDsaSig.length !== ML_DSA_65_SIGNATURE_SIZE) {
        throw new Error(`ML-DSA-65 signature must be ${ML_DSA_65_SIGNATURE_SIZE} bytes, got ${mlDsaSig.length}`);
    }
    return concatBytes(HYBRID_SIGNATURE_PREFIX, ed25519Sig, mlDsaSig);
}
function splitHybridSignature(signature) {
    if (!isHybridSignatureEncoding(signature)) {
        return null;
    }
    return {
        ed25519: signature.subarray(ED25519_OFFSET, ED25519_OFFSET + ED25519_SIGNATURE_SIZE),
        mlDsa: signature.subarray(ML_DSA_OFFSET),
    };
}
/**
 * Verifies a hybrid signature. Both halves must succeed over the same preimage.
 * Missing or wrong-size PQ public key fails closed (never skips ML-DSA).
 */
export function verifyHybrid(ed25519PublicKey, mlDsa65PublicKey, message, signature) {
    const parts = splitHybridSignature(signature);
    if (!parts) {
        return false;
    }
    if (ed25519PublicKey.length !== ED25519_PUBLIC_KEY_SIZE ||
        !mlDsa65PublicKey ||
        mlDsa65PublicKey.length !== ML_DSA_65_PUBLIC_KEY_SIZE) {
        return false;
    }
    const edOk = verifyEd25519(ed25519PublicKey, message, parts.ed25519);
    let pqOk = false;
    try {
        pqOk = ml_dsa65.verify(mlDsa65PublicKey, message, parts.mlDsa);
    }
    catch {
        pqOk = false;
    }
    return edOk && pqOk;
}
/**
 * Dispatches on encoding: 64-byte Ed25519 history, hybrid AND, else false.
 * Hybrid encoding with a missing PQ public key fails closed.
 */
export function verifySignature(ed25519PublicKey, message, signature, mlDsa65PublicKey) {
    if (signature.length === ED25519_SIGNATURE_SIZE) {
        return verifyEd25519(ed25519PublicKey, message, signature);
    }
    if (isHybridSignatureEncoding(signature)) {
        return verifyHybrid(ed25519PublicKey, mlDsa65PublicKey, message, signature);
    }
    return false;
}
/**
 * Signs with Ed25519 only, or Ed25519+ML-DSA-65 when a PQ secret is provided.
 * New identity sends pass the ML-DSA-65 secret; history/tests may omit it.
 */
export function signIdentity(ed25519PrivateKey, message, mlDsa65SecretKey) {
    if (mlDsa65SecretKey) {
        return signHybrid(ed25519PrivateKey, mlDsa65SecretKey, message);
    }
    return signEd25519(ed25519PrivateKey, message);
}
/**
 * Signs concatenated chunks with the hybrid combiner.
 */
export function signHybridChunks(ed25519PrivateKey, mlDsa65SecretKey, chunks) {
    return signHybrid(ed25519PrivateKey, mlDsa65SecretKey, concatBytes(...chunks));
}
/**
 * Verifies a hybrid signature over concatenated chunks.
 */
export function verifyHybridChunks(ed25519PublicKey, mlDsa65PublicKey, chunks, signature) {
    return verifyHybrid(ed25519PublicKey, mlDsa65PublicKey, concatBytes(...chunks), signature);
}
//# sourceMappingURL=hybrid.js.map